import { zodResolver } from '@hookform/resolvers/zod';
import {
  PACKAGING_UNIT_CODES,
  packagingLabel,
  productPackagingsWriteSchema,
  type ProductDetail,
  type ProductPackagingWriteInput,
} from '@erp/shared';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePackagingTypes } from '@/features/settings';
import { useFeatureAvailability } from '@/features/platform-features';
import { z } from 'zod';
import { FormField } from '@/components/common/form-field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateProduct } from '@/features/products/api/use-products';
import { CategorizedCustomFields, useCustomFieldDefinitions } from '@/features/custom-fields';
import { numberOrNull, textOrNull } from '@/features/products/lib/form-value';
import type { ApiError } from '@/lib/api-client';
import { toastApiError } from '@/lib/api-error-toast';

const formSchema = z.object({ packagings: productPackagingsWriteSchema });

type FormValues = { packagings: ProductPackagingWriteInput[] };

function toFormValues(product: ProductDetail): FormValues {
  return {
    packagings: product.packagings.map((packaging) => ({
      id: packaging.id,
      packagingTypeId: packaging.packagingTypeId ?? null,
      isSummaryDefault: packaging.isSummaryDefault ?? false,
      isWeightDefault: packaging.isWeightDefault ?? false,
      code: packaging.code,
      name: packaging.name,
      unitCode: packaging.unitCode,
      factor: packaging.factor,
      ean: packaging.ean,
      isSalesDefault: packaging.isSalesDefault,
      isPurchaseDefault: packaging.isPurchaseDefault,
      weightGrossKg: packaging.weightGrossKg,
      lengthMm: packaging.lengthMm,
      widthMm: packaging.widthMm,
      heightMm: packaging.heightMm,
      minOrderQuantity: packaging.minOrderQuantity,
      orderStepQuantity: packaging.orderStepQuantity,
      customFields: packaging.customFields,
    })),
  };
}

const emptyToNull = { setValueAs: textOrNull };
const numberOrNullSetter = { setValueAs: numberOrNull };
export interface PackagingEditorProps {
  product: ProductDetail;
  canWrite: boolean;
}

export function PackagingEditor({ product, canWrite }: PackagingEditorProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { isEnabled } = useFeatureAvailability();
  const summaryEnabled = isEnabled('packaging_summary');
  const types = usePackagingTypes(summaryEnabled);
  const [saveError, setSaveError] = useState<string | null>(null);
  const updateProduct = useUpdateProduct(product.id);
  const definitions = useCustomFieldDefinitions('PRODUCT_PACKAGING');
  const [customFieldErrors, setCustomFieldErrors] = useState<Readonly<Record<string, string>>>({});

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: toFormValues(product),
  });

  const fieldArray = useFieldArray({ control: form.control, name: 'packagings', keyName: 'key' });

  useEffect(() => {
    form.reset(toFormValues(product));
    setCustomFieldErrors({});
  }, [product, form]);

  useEffect(() => {
    if (definitions.data === undefined) return;
    const current = form.getValues('packagings');
    let changed = false;
    const next = current.map((packaging) => {
      const customFields = { ...(packaging.customFields ?? {}) };
      for (const definition of definitions.data) {
        if (customFields[definition.key] === undefined && definition.defaultValue !== undefined) {
          customFields[definition.key] = definition.defaultValue;
          changed = true;
        }
      }
      return { ...packaging, customFields };
    });
    if (changed) form.setValue('packagings', next);
  }, [definitions.data, form, product]);

  const onSubmit = form.handleSubmit((values) => {
    setCustomFieldErrors({});
    setSaveError(null);
    updateProduct.mutate(
      { packagings: values.packagings },
      {
        onSuccess: () => toast.success(t('products.packagings.saved')),
        onError: (error: ApiError) => {
          setSaveError(error.message);
          setCustomFieldErrors(
            Object.fromEntries(
              (error.validationErrors ?? []).map((issue) => [issue.fieldKey, issue.reason]),
            ),
          );
          toastApiError(error, t('products.packagings.saveError'));
        },
      },
    );
  });

  const addPackaging = (): void => {
    fieldArray.append({
      code: '',
      name: '',
      unitCode: 'BX',
      factor: '1',
      ean: null,
      isSalesDefault: false,
      isPurchaseDefault: false,
      weightGrossKg: null,
      lengthMm: null,
      widthMm: null,
      heightMm: null,
      minOrderQuantity: null,
      orderStepQuantity: null,
      customFields: Object.fromEntries(
        (definitions.data ?? []).flatMap((definition) =>
          definition.defaultValue === undefined
            ? []
            : [[definition.key, definition.defaultValue] as const],
        ),
      ),
    });
  };

  const errors = form.formState.errors;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      {saveError || Object.keys(errors).length ? (
        <p role="alert" className="text-destructive">
          {saveError ?? t('shipping.saveError')}
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">{t('shipping.weightHint')}</p>
      {types.isError ? (
        <div role="alert">
          {t('shipping.loadError')}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void types.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {t('products.packagings.description', {
          unit: t(`products.units.${product.baseUnit}`, { defaultValue: product.baseUnit }),
        })}
      </p>

      {fieldArray.fields.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-md border border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('products.packagings.empty')}</p>
          {canWrite ? (
            <Button type="button" size="sm" onClick={addPackaging}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('products.packagings.add')}
            </Button>
          ) : null}
        </div>
      ) : (
        <ul className="flex flex-col gap-4">
          {fieldArray.fields.map((field, index) => {
            const rowError = errors.packagings?.[index];
            return (
              <li key={field.key} className="rounded-md border border-border p-4">
                {summaryEnabled ? (
                  <div className="mb-4 grid gap-3 sm:grid-cols-2">
                    <FormField id={`pack-type-${index}`} label={t('shipping.packagingType')}>
                      <select
                        id={`pack-type-${index}`}
                        className="h-11 w-full rounded-md border border-input bg-background px-3"
                        disabled={!canWrite || types.isPending || types.isError}
                        value={form.watch(`packagings.${index}.packagingTypeId`) ?? ''}
                        onChange={(event) =>
                          form.setValue(
                            `packagings.${index}.packagingTypeId`,
                            event.target.value || null,
                            { shouldDirty: true },
                          )
                        }
                      >
                        <option value="">{t('shipping.unassigned')}</option>
                        {types.data?.data
                          .filter(
                            (type) =>
                              type.isActive ||
                              type.id === form.watch(`packagings.${index}.packagingTypeId`),
                          )
                          .map((type) => (
                            <option key={type.id} value={type.id}>
                              {packagingLabel(type, i18n.language) ?? type.code}
                            </option>
                          ))}
                      </select>
                    </FormField>
                    <div>
                      {(['isSummaryDefault', 'isWeightDefault'] as const).map((key) => (
                        <label key={key} className="flex min-h-11 items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            disabled={!canWrite}
                            checked={form.watch(`packagings.${index}.${key}`) === true}
                            onChange={(event) =>
                              form.setValue(`packagings.${index}.${key}`, event.target.checked, {
                                shouldDirty: true,
                              })
                            }
                          />
                          {t(
                            key === 'isSummaryDefault'
                              ? 'shipping.summaryDefault'
                              : 'shipping.weightDefault',
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <FormField
                    id={`pack-code-${index}`}
                    label={t('products.packagings.fields.code')}
                    required
                    error={rowError?.code?.message}
                  >
                    <Input
                      id={`pack-code-${index}`}
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.code`)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-name-${index}`}
                    label={t('products.packagings.fields.name')}
                    required
                    error={rowError?.name?.message}
                  >
                    <Input
                      id={`pack-name-${index}`}
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.name`)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-unit-${index}`}
                    label={t('products.packagings.fields.unitCode')}
                    required
                  >
                    <Select
                      value={form.watch(`packagings.${index}.unitCode`) ?? 'BX'}
                      disabled={!canWrite}
                      onValueChange={(value) =>
                        form.setValue(`packagings.${index}.unitCode`, value, { shouldDirty: true })
                      }
                    >
                      <SelectTrigger id={`pack-unit-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PACKAGING_UNIT_CODES.map((code) => (
                          <SelectItem key={code} value={code}>
                            {t(`products.packagingUnits.${code}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <FormField
                    id={`pack-factor-${index}`}
                    label={t('products.packagings.fields.factor')}
                    description={t('products.packagings.fields.factorHint', {
                      unit: t(`products.units.${product.baseUnit}`, {
                        defaultValue: product.baseUnit,
                      }),
                    })}
                    required
                    error={rowError?.factor?.message}
                  >
                    <Input
                      id={`pack-factor-${index}`}
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.factor`)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-ean-${index}`}
                    label={t('products.packagings.fields.ean')}
                    error={rowError?.ean?.message}
                  >
                    <Input
                      id={`pack-ean-${index}`}
                      inputMode="numeric"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.ean`, emptyToNull)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-weight-${index}`}
                    label={t('products.packagings.fields.weightGrossKg')}
                    error={rowError?.weightGrossKg?.message}
                  >
                    <Input
                      id={`pack-weight-${index}`}
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.weightGrossKg`, emptyToNull)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-min-${index}`}
                    label={t('products.packagings.fields.minOrderQuantity')}
                    error={rowError?.minOrderQuantity?.message}
                  >
                    <Input
                      id={`pack-min-${index}`}
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.minOrderQuantity`, emptyToNull)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-step-${index}`}
                    label={t('products.packagings.fields.orderStepQuantity')}
                    error={rowError?.orderStepQuantity?.message}
                  >
                    <Input
                      id={`pack-step-${index}`}
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.orderStepQuantity`, emptyToNull)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-length-${index}`}
                    label={t('products.packagings.fields.lengthMm')}
                    error={rowError?.lengthMm?.message}
                  >
                    <Input
                      id={`pack-length-${index}`}
                      type="number"
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.lengthMm`, numberOrNullSetter)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-width-${index}`}
                    label={t('products.packagings.fields.widthMm')}
                    error={rowError?.widthMm?.message}
                  >
                    <Input
                      id={`pack-width-${index}`}
                      type="number"
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.widthMm`, numberOrNullSetter)}
                    />
                  </FormField>

                  <FormField
                    id={`pack-height-${index}`}
                    label={t('products.packagings.fields.heightMm')}
                    error={rowError?.heightMm?.message}
                  >
                    <Input
                      id={`pack-height-${index}`}
                      type="number"
                      className="text-right tabular-nums"
                      disabled={!canWrite}
                      {...form.register(`packagings.${index}.heightMm`, numberOrNullSetter)}
                    />
                  </FormField>

                  <div className="flex flex-col justify-end gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`pack-sales-${index}`}
                        disabled={!canWrite}
                        checked={form.watch(`packagings.${index}.isSalesDefault`) === true}
                        onCheckedChange={(checked) =>
                          form.setValue(`packagings.${index}.isSalesDefault`, checked === true, {
                            shouldDirty: true,
                          })
                        }
                      />
                      <Label htmlFor={`pack-sales-${index}`} className="font-normal">
                        {t('products.packagings.fields.isSalesDefault')}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`pack-purchase-${index}`}
                        disabled={!canWrite}
                        checked={form.watch(`packagings.${index}.isPurchaseDefault`) === true}
                        onCheckedChange={(checked) =>
                          form.setValue(`packagings.${index}.isPurchaseDefault`, checked === true, {
                            shouldDirty: true,
                          })
                        }
                      />
                      <Label htmlFor={`pack-purchase-${index}`} className="font-normal">
                        {t('products.packagings.fields.isPurchaseDefault')}
                      </Label>
                    </div>
                  </div>
                </div>

                {definitions.data === undefined || definitions.data.length === 0 ? null : (
                  <CategorizedCustomFields
                    definitions={definitions.data}
                    values={form.watch(`packagings.${index}.customFields`) ?? {}}
                    mode={canWrite ? 'edit' : 'detail'}
                    disabled={!canWrite}
                    errors={Object.fromEntries(
                      Object.entries(customFieldErrors).flatMap(([key, message]) => {
                        const prefix = `${String(index)}.`;
                        return key.startsWith(prefix) ? [[key.slice(prefix.length), message]] : [];
                      }),
                    )}
                    idPrefix={`packaging-${index}-custom-field`}
                    className="mt-4"
                    onChange={(key, value) =>
                      form.setValue(
                        `packagings.${index}.customFields`,
                        {
                          ...form.getValues(`packagings.${index}.customFields`),
                          [key]: value,
                        },
                        { shouldDirty: true },
                      )
                    }
                  />
                )}

                {canWrite ? (
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => fieldArray.remove(index)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      {t('products.packagings.remove')}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2">
          {fieldArray.fields.length === 0 ? null : (
            <Button type="button" variant="outline" size="sm" onClick={addPackaging}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('products.packagings.add')}
            </Button>
          )}
          <Button type="submit" size="sm" disabled={updateProduct.isPending}>
            {updateProduct.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : null}
            {t('common.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
